/**
 * PDF Inline Viewer
 * Renders PDF pages using pdfjs for native WebView support
 */

import { useEffect, useRef, useState, type RefObject } from 'react'
import { Loader2, cn } from '@ufc/uikit'
import { getDocument, GlobalWorkerOptions, type PDFDocumentProxy } from 'pdfjs-dist'
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url'

GlobalWorkerOptions.workerSrc = workerSrc

interface PdfInlineViewerProps {
  blob?: Blob | null
  className?: string
  onError?: (message?: string) => void
}

interface RenderState {
  isRendering: boolean
}

interface RenderContext {
  container: HTMLDivElement
  blob: Blob
  onError?: (message?: string) => void
  setRenderState: (state: RenderState) => void
  cancelToken: { cancelled: boolean }
}

function createInitialRenderState(): RenderState {
  return { isRendering: false }
}

function setRenderingState(setState: (state: RenderState) => void, isRendering: boolean): void {
  setState({ isRendering })
}

function clearContainer(container: HTMLDivElement): void {
  container.innerHTML = ''
}

function createPdfBytesFromArrayBuffer(buffer: ArrayBuffer): Uint8Array {
  return new Uint8Array(buffer)
}

function readBlobAsArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer()
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result)
        return
      }
      reject(new Error('Failed to read PDF'))
    }
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read PDF'))
    reader.readAsArrayBuffer(blob)
  })
}

function createPdfBytes(blob: Blob): Promise<Uint8Array> {
  return readBlobAsArrayBuffer(blob).then((buffer) => createPdfBytesFromArrayBuffer(buffer))
}

function loadPdfDocument(bytes: Uint8Array): Promise<PDFDocumentProxy> {
  return getDocument({ data: bytes }).promise
}

function getContainerWidth(container: HTMLDivElement): number {
  const width = container.clientWidth
  return width > 0 ? width : 1
}

function getViewportScale(pageWidth: number, containerWidth: number): number {
  if (pageWidth <= 0) return 1
  return containerWidth / pageWidth
}

function createCanvas(): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.className = 'w-full h-auto block'
  return canvas
}

async function renderPageToCanvas(params: {
  pageNumber: number
  pdf: PDFDocumentProxy
  container: HTMLDivElement
  cancelToken: { cancelled: boolean }
}): Promise<void> {
  const { pageNumber, pdf, container, cancelToken } = params
  if (cancelToken.cancelled) return

  const page = await pdf.getPage(pageNumber)
  if (cancelToken.cancelled) return

  const viewport = page.getViewport({ scale: 1 })
  const scale = getViewportScale(viewport.width, getContainerWidth(container))
  const scaledViewport = page.getViewport({ scale })

  const canvas = createCanvas()
  const context = canvas.getContext('2d')

  if (!context) {
    throw new Error('Unable to render PDF')
  }

  canvas.width = Math.floor(scaledViewport.width)
  canvas.height = Math.floor(scaledViewport.height)

  container.appendChild(canvas)

  const renderTask = page.render({
    canvasContext: context,
    viewport: scaledViewport,
  })

  await renderTask.promise
}

async function renderPdfToContainer(context: RenderContext): Promise<void> {
  const { container, blob, onError, setRenderState, cancelToken } = context

  try {
    setRenderingState(setRenderState, true)
    clearContainer(container)

    const bytes = await createPdfBytes(blob)
    if (cancelToken.cancelled) return

    const pdf = await loadPdfDocument(bytes)
    if (cancelToken.cancelled) {
      pdf.destroy()
      return
    }

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      if (cancelToken.cancelled) break
      await renderPageToCanvas({
        pageNumber,
        pdf,
        container,
        cancelToken,
      })
    }

    pdf.destroy()
  } catch (error) {
    if (!cancelToken.cancelled) {
      onError?.(error instanceof Error ? error.message : undefined)
    }
  } finally {
    if (!cancelToken.cancelled) {
      setRenderingState(setRenderState, false)
    }
  }
}

function shouldRenderPdf(blob: Blob | null | undefined, container: HTMLDivElement | null): boolean {
  return !!blob && !!container
}

function runPdfRenderEffect(params: {
  blob: Blob | null | undefined
  containerRef: RefObject<HTMLDivElement | null>
  onErrorRef: RefObject<((message?: string) => void) | undefined>
  setRenderState: (state: RenderState) => void
}): () => void {
  const cancelToken = { cancelled: false }
  const container = params.containerRef.current

  if (shouldRenderPdf(params.blob, container)) {
    void renderPdfToContainer({
      container: container as HTMLDivElement,
      blob: params.blob as Blob,
      onError: params.onErrorRef.current,
      setRenderState: params.setRenderState,
      cancelToken,
    })
  } else {
    setRenderingState(params.setRenderState, false)
  }

  return () => {
    cancelToken.cancelled = true
    if (container) {
      clearContainer(container)
    }
  }
}

function shouldShowLoader(renderState: RenderState): boolean {
  return renderState.isRendering
}

export function PdfInlineViewer({ blob, className, onError }: PdfInlineViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [renderState, setRenderState] = useState<RenderState>(createInitialRenderState())
  const onErrorRef = useRef(onError)

  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  useEffect(() => runPdfRenderEffect({
    blob,
    containerRef,
    onErrorRef,
    setRenderState,
  }), [blob])

  return (
    <div className={cn('relative w-full', className)}>
      {shouldShowLoader(renderState) && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/70">
          <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
        </div>
      )}
      <div ref={containerRef} className="flex flex-col gap-4" />
    </div>
  )
}
